/**
 * Desktop port of the native first-run onboarding
 * (`apps/native/src/onboarding`). The shapes are deliberately the same as
 * native's `types.ts` so the two flows can be read side by side — and so the
 * business branch can be dropped in later without reshaping the answers.
 *
 * Both paths are built: individual, and the business branch
 * (`BUSINESS_CLAIM` → `CLAIM_SUBMITTED` → `BUSINESS_CITY`). The three-page CMS
 * `WELCOME` intro is declared but has no screen — @see `IMPLEMENTED_STEPS` in
 * `constants.ts`, which is what stops the flow navigating to it.
 */

/** One logical step of the onboarding flow. */
export type OnboardingStepName =
  /** The three-page CMS intro. Declared, not built — @see `IMPLEMENTED_STEPS`. */
  | "WELCOME"
  /**
   * Step 1 — "Hi there! 👋 Let's get started" (`15984:127545`). The fork on
   * native: answering "Business use" sends the user down the claim branch.
   */
  | "USER_TYPE"
  /** Step 2 — "Who are you here for?". */
  | "AUDIENCE"
  /** Step 3 — "What should we look for first?". */
  | "ACCESS_NEEDS"
  /**
   * The last counted step (4 of 4 signed in, 5 of 5 signed out) — "And where
   * should we start?". Picking a city ends the flow when signed in, and moves
   * on to `JUMP_IN` when signed out.
   */
  | "CITY"
  /**
   * "Let’s jump in!" (`15666:345509`, native's `AllSetSheet`) — signed-out
   * only, straight after the city: "Sign up now" or "I’ll do it later". Not
   * counted. Either answer ends the flow.
   */
  | "JUMP_IN"
  /**
   * "How did you hear about us?" — the last question of sign-up
   * (`ONBOARDING_SIGN_UP_STEPS`, `16150:279245`), and step 4 of 5 of the main
   * flow for a signed-out visitor (`ONBOARDING_SEQUENCE_SIGNED_OUT`).
   */
  | "HEAR_ABOUT"
  /**
   * Business branch — "Let’s find your business" (`16224:292764`). Reached
   * instead of `AUDIENCE` when step 1 is answered "Business use".
   */
  | "BUSINESS_CLAIM"
  /** Business branch — "We’ll be in touch!" (`16224:294787`). Not counted. */
  | "CLAIM_SUBMITTED"
  /**
   * Business branch — "Let’s explore!", ported from native
   * (`BusinessCityScreen`, `15666:346394`): the city derived from the claimed
   * business, confirmed or changed. Picking a city ends the flow.
   */
  | "BUSINESS_CITY";

/**
 * Step 1 — how the user plans to use Sociability. `business` forks the flow
 * onto the claim branch — @see `sequenceForAnswers`.
 */
export type OnboardingUserType = "individual" | "business";

/** Step 2 — who the user is answering for. Individual path only. */
export type OnboardingAudience = "myself" | "someone_i_care_for";

/** A city chosen on the last step. Selecting one completes onboarding. */
export type OnboardingCity = {
  name: string;
  country: string;
  /**
   * Filled in from `resolve-suggestion-area` when the tap resolves. Optional
   * because resolution is a network call the flow must not dead-end on — a
   * failed resolve still completes onboarding, just without coordinates.
   */
  latitude?: number;
  longitude?: number;
  placeId?: string | null;
  /**
   * Short display label from the resolver, e.g. "London" - what the home
   * header shows. Falls back to `name` when absent.
   *
   * A record of what was chosen, not the thing home reads: `CityStep` copies
   * it into `manualLocationAtom` when the city is picked, and that store is
   * what points the page - @see `useApplyManualLocation`.
   */
  label?: string;
  /**
   * Whether Sociability already has accessibility data for this city. Same
   * signal the picker colours its pin from — `data.covered` on a suggestion,
   * equivalently `venueCount === null`.
   */
  covered?: boolean;
  /**
   * Venues here we hold accessibility information for. Absent rather than zero
   * when unknown: a covered city whose count didn't come back should say
   * nothing, not claim it has none.
   */
  venueCount?: number;
};

/**
 * "Your role in the business" chips. The values are what the claim endpoint
 * expects for `business_role`; `others` pairs with `business_role_other`.
 *
 * Note the plural — the enum is `owner | general_manager | others`, and
 * `other` is rejected with "The selected business role is invalid."
 */
export type OnboardingBusinessRole = "owner" | "general_manager" | "others";

/**
 * What the business-name search settled on, once `place-from-google` (lookup
 * mode) had classified the place.
 *
 * `businessId` decides which half of the claim payload this becomes: a
 * business already on Sociability is claimed by `business_id`; anything else
 * is described by `suggested_business_data[…]`. Alternatives — never both.
 */
export type OnboardingBusinessMatch = {
  placeId: string | null;
  /**
   * Set only when the place is already ours, from `venue.business.id` — the
   * numeric id the claim endpoint checks. **Not** `venue.id`, a Google hash
   * that fails that check.
   */
  businessId: string | null;
  /**
   * False when `place-from-google` classified the place as a category
   * Sociability does not list. The claim still goes through; the confirmation
   * says the business is not visible yet.
   */
  isSupported: boolean;
  /** → `suggested_business_data[formatted_address]`. */
  address: string;
  /** Where the place is — what "Let’s explore!" derives the city from. */
  latitude?: number;
  longitude?: number;
};

/** The "Let’s find your business" form. Mirrors native, minus the dropped chain question. */
export type OnboardingBusinessClaim = {
  contactName: string;
  email: string;
  /** Optional on the form. */
  phone: string;
  /** Undefined until a chip is picked — there is no default role. */
  role?: OnboardingBusinessRole;
  /** Only collected when `role` is `others` → `business_role_other`. */
  roleOther: string;
  /** A name picked from the search, or free text kept as typed. */
  businessName: string;
  /** The resolved place behind `businessName`; absent for free text. */
  business?: OnboardingBusinessMatch;
  /** Free text → `description`. */
  description: string;
};

export const EMPTY_BUSINESS_CLAIM: OnboardingBusinessClaim = {
  contactName: "",
  email: "",
  phone: "",
  roleOther: "",
  businessName: "",
  description: "",
};

/** Everything the user told us during onboarding. */
export type OnboardingAnswers = {
  /** Step 1. Also the branch selector on native — @see `sequenceForAnswers`. */
  userType?: OnboardingUserType;
  /** Step 2. Not asked on the business branch. */
  audience?: OnboardingAudience;
  /**
   * Access-need group names from step 3. **At most one** — the step is a radio
   * group, so picking a row replaces the previous pick; the array is kept
   * because it is what every reader of the answer takes, and because "none" is
   * the empty one. Empty means either "not answered yet" or a step the user got
   * through with nothing to pick (the rows failed to load) —
   * `accessNeedsAnswered` distinguishes the two.
   */
  accessNeeds: string[];
  accessNeedsAnswered: boolean;
  city?: OnboardingCity;
  /** "How did you hear about us?". Sent with the sign-up, then kept only as a record. */
  hearAbout?: OnboardingHearAbout;
  /**
   * The claim as submitted, business branch only. Written once the backend has
   * accepted it, so its presence means the claim was filed.
   */
  businessClaim?: OnboardingBusinessClaim;
};

/**
 * The picked "How did you hear about us?" row. A `group` normally; an
 * `option` only while the backend has no groups to offer - @see
 * `HearAboutUsGroup` - and the step falls back to the flat option list.
 */
export type OnboardingHearAbout = {
  kind: "group" | "option";
  id: number;
  /** Only for a group that `allows_other_text`. */
  otherText?: string;
  /**
   * The row's title when it was picked — only for analytics' `referral_source`,
   * so completion does not have to reload the rows to name the answer.
   */
  label?: string;
};

export const EMPTY_ONBOARDING_ANSWERS: OnboardingAnswers = {
  accessNeeds: [],
  accessNeedsAnswered: false,
};
